import React from 'react'

import UserAvatar, { AvatarableUser } from '@/components/misc/UserAvatar'
import UserAvatarsStrip from '@/components/misc/UserAvatarsStrip'

interface IntroducedToUser {
  fullName: string
  email: string
}

interface Props {
  /** Retained for backwards compatibility; no longer displayed */
  title?: string
  users: IntroducedToUser[]
  toCompany: { isUnrealizedCompany?: boolean }
}

function getInitialsFromName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]

  if (!first) {
    return ''
  }

  const last = parts.length > 1 ? parts[parts.length - 1] : undefined

  return (first.charAt(0) + (last?.charAt(0) ?? '')).toUpperCase()
}

/**
 * Build the avatar model for a user. When we only have an email address (an
 * unrealized company), seed the avatar off the email and use its first letter
 * as the initial.
 */
function toAvatarableUser(
  user: IntroducedToUser,
  useEmail: boolean,
): AvatarableUser {
  if (useEmail) {
    return {
      fullName: user.email,
      avatar: null,
      initials: user.email.charAt(0),
    }
  }

  return {
    fullName: user.fullName,
    avatar: null,
    initials: getInitialsFromName(user.fullName),
  }
}

const ReferralIntroducedTo: React.FC<Props> = ({ toCompany, users }) => {
  // Unrealized companies have no realized users, so we only have their emails.
  const useEmail = !!toCompany?.isUnrealizedCompany

  const displayUsers = users.map((user) => ({
    key: user.email,
    label: useEmail ? user.email : user.fullName,
    avatarUser: toAvatarableUser(user, useEmail),
  }))

  function renderUser({
    key,
    label,
    avatarUser,
  }: (typeof displayUsers)[number]) {
    return (
      <span key={key} className="inline-flex items-center gap-1">
        <UserAvatar user={avatarUser} size="default" />
        {label}
      </span>
    )
  }

  if (displayUsers.length === 0) {
    return null
  }

  // A single user: show them inline as [avatar] [name].
  if (displayUsers.length === 1) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-3">
        {displayUsers.map(renderUser)}
      </div>
    )
  }

  // Multiple users: show an overlapping avatar strip, with a tooltip listing
  // everyone as [avatar] [name].
  return (
    <UserAvatarsStrip
      size="default"
      users={displayUsers.map((user) => user.avatarUser)}
      tooltip={
        <div className="flex flex-col gap-1">
          {displayUsers.map(renderUser)}
        </div>
      }
    />
  )
}

export default ReferralIntroducedTo
